import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthUser } from '../services/auth';
import { authService } from '../services/auth';
import { uploadProfilePhoto } from '../services/storage';
import './UserProfileDropdown.css';

interface UserProfilePopupProps {
  user: AuthUser;
  onClose: () => void;
  onProfileUpdate?: (updatedUser: AuthUser) => void;
}

// Helper function to compress images
const compressImage = (dataUrl: string, maxSizeKB: number = 100): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      // Create canvas
      const canvas = document.createElement('canvas');
      let width = image.width;
      let height = image.height;
      
      // Calculate new dimensions while maintaining aspect ratio
      const maxDimension = 300; // Limit max dimension to 300px
      if (width > height && width > maxDimension) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else if (height > maxDimension) {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw image on canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      ctx.drawImage(image, 0, 0, width, height);
      
      // Start with high quality
      let quality = 0.9;
      let result = canvas.toDataURL('image/jpeg', quality);
      
      // Reduce quality until size is under limit
      while (result.length > maxSizeKB * 1024 && quality > 0.1) {
        quality -= 0.1;
        result = canvas.toDataURL('image/jpeg', quality);
      }
      
      resolve(result);
    };
    
    image.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    image.src = dataUrl;
  });
};

export const UserProfilePopup: React.FC<UserProfilePopupProps> = ({ 
  user, 
  onClose,
  onProfileUpdate 
}) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [photoURL, setPhotoURL] = useState(user.photoURL || '');
  const [tempPhotoURL, setTempPhotoURL] = useState(''); // For preview only
  const fileInputRef = useRef<HTMLInputElement>(null);
  const defaultAvatar = '/default_avatar2.svg';

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setSaveError(t('userProfile.errors.imageMustBeSmaller'));
        return;
      }
      
      const url = URL.createObjectURL(file);
      setTempPhotoURL(url);
      setSaveError('');
      setSaveSuccess(false);
    }
  };

  const handleRemovePhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    setPhotoURL('');
    setTempPhotoURL('');
    setSaveError('');
    setSaveSuccess(false);
  };

  const displayPhotoURL = tempPhotoURL || photoURL || defaultAvatar;

  const handleSave = async () => {
    if (!displayName.trim()) {
      setSaveError(t('userProfile.errors.nameCannotBeEmpty'));
      return;
    }

    try {
      setIsSaving(true);
      setSaveError('');
      
      let downloadURL = photoURL;
      let photoWasRemoved = false;
      
      // If we have a temporary photo URL (new upload), process and upload it
      if (tempPhotoURL) {
        try {
          // Compress the image
          const compressedImage = await compressImage(tempPhotoURL, 100);
          
          // Upload to Firebase Storage
          downloadURL = await uploadProfilePhoto(compressedImage);
        } catch (error) {
          console.error('Error processing image:', error);
          setSaveError(t('userProfile.errors.errorProcessingImage'));
          setIsSaving(false);
          return;
        }
      }
      
      // If user has removed their photo, explicitly set downloadURL to empty string
      // Both photoURL and tempPhotoURL being empty means user wants to remove the photo
      if (!photoURL && !tempPhotoURL) {
        downloadURL = '';
        photoWasRemoved = true;
      }
      
      // Update user profile with the new name and photo URL
      await authService.updateProfile(displayName, downloadURL);
      
      // Force a reload of auth user data
      try {
        await authService.refreshUser();
      } catch (error) {
        console.error('Error refreshing user data:', error);
        // Continue even if refresh fails - the update was successful
      }
      
      // Create updated user object
      const updatedUser: AuthUser = {
        ...user,
        displayName,
        photoURL: downloadURL || null
      };
      
      // Call the callback if it exists
      if (onProfileUpdate) {
        onProfileUpdate(updatedUser);
      }
      
      setSaveSuccess(true);
      setTempPhotoURL(''); // Clear the temporary URL
      setPhotoURL(downloadURL); // Update the photoURL with the new URL
      setIsSaving(false);
      
      // Auto close after 1.5 seconds on successful save
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveError(t('userProfile.errors.failedToUpdateProfile'));
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-popup-overlay" onClick={onClose}>
      <div className="profile-popup-content settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>{t('userProfile.settings')}</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            style={{ 
              color: '#a0a0a0',
              backgroundColor: 'transparent',
              outline: 'none',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#a0a0a0';
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.textDecoration = 'none';
            }}
          >×</button>
        </div>
        
        <div className="settings-content">
          <div className="profile-settings">
            <div className="settings-section">
              <h3 className="settings-section-title">{t('userProfile.yourPhoto')}</h3>
              <div className="profile-photo-section">
                <div className="profile-avatar-container">
                  <img 
                    src={displayPhotoURL} 
                    alt="User Avatar" 
                    className="profile-avatar"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = defaultAvatar;
                    }}
                  />
                </div>
                <div className="photo-info" style={{ padding: 0, paddingTop: '4px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <table cellSpacing="0" cellPadding="0" style={{ border: 'none', borderCollapse: 'collapse', width: 'auto' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '0 0 6px 0', paddingLeft: 0, whiteSpace: 'nowrap' }}>
                          <a 
                            href="#" 
                            className="photo-action" 
                            style={{ 
                              padding: 0, 
                              margin: 0, 
                              textIndent: 0, 
                              display: 'inline-block',
                              color: '#111111'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.color = '#07F77F'}
                            onClick={(e) => {
                              e.preventDefault();
                              handleUploadClick();
                            }}
                          >
                            {t('userProfile.uploadNewPhoto')}
                          </a>
                          <input 
                            type="file" 
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                          <span className="photo-action-separator">|</span>
                          <a 
                            href="#" 
                            className="photo-action" 
                            style={{ 
                              padding: 0, 
                              margin: 0, 
                              textIndent: 0, 
                              display: 'inline-block',
                              color: '#111111'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.color = '#07F77F'}
                            onClick={handleRemovePhoto}
                          >
                            {t('userProfile.removePhoto')}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: 0 }}>
                          <div className="photo-description">{t('userProfile.photoDescription')}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="settings-section">
              <div className="settings-field">
                <label className="settings-label" htmlFor="fullName">
                  {t('userProfile.yourFullName')}
                  <span className="required-indicator">*</span>
                </label>
                <input 
                  id="fullName"
                  type="text" 
                  className="settings-input" 
                  value={displayName}
                  onChange={handleNameChange}
                />
                {saveError && <div className="settings-error">{saveError}</div>}
                {saveSuccess && <div className="settings-success">{t('userProfile.profileUpdatedSuccessfully')}</div>}
              </div>
              
              <div className="settings-field">
                <label className="settings-label" htmlFor="email">{t('userProfile.email')}</label>
                <input 
                  id="email"
                  type="email" 
                  className="settings-input" 
                  value={user.email || ''}
                  readOnly 
                />
              </div>
              
              <div className="settings-actions">
                <button 
                  className="save-button" 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? t('userProfile.saving') : t('userProfile.saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 